<template>
    <div class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h3 class="text-2xl font-bold">{{ $translate('Edit Shipping Address') }}</h3>
                <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            
            <div class="info-alert" role="alert">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="font-medium">{{ $translate('Note') }}</p>
                        <p>{{ $translate('Since some items require delivery, please provide the information needed for ' +
                        'shipping your items.') }}</p>
                    </div>
                </div>
            </div>
            
            <form @submit.prevent="saveShippingAddress">
                <div class="form-grid">
                    <div class="col-span-2">
                        <label for="ship-name">{{ $translate('Name or Business Name') }}</label>
                        <input id="ship-name" v-model="localAddress.name" type="text" required />
                    </div>
                    
                    <div class="col-span-2">
                        <label for="ship-address">{{ $translate('Address') }}</label>
                        <input id="ship-address" v-model="localAddress.address" type="text" required />
                    </div>
                    
                    <div>
                        <label for="ship-city">{{ $translate('City') }}</label>
                        <input id="ship-city" v-model="localAddress.city" type="text" required />
                    </div>
                    
                    <div>
                        <label for="ship-zip">{{ $translate('Zip Code') }}</label>
                        <input id="ship-zip" v-model="localAddress.zip_code" type="text" required />
                    </div>
                    
                    <div class="col-span-2 relative">
                        <label for="ship-country">{{ $translate('Country') }}</label>
                        <select id="ship-country" v-model="localAddress.country_id" required class="appearance-none">
                            <option value="58">{{ $translate('Czech Republic') }}</option>
                            <option value="200">{{ $translate('Slovak Republic') }}</option>
                        </select>
                        <div class="select-arrow">
                            <svg class="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                        </div>
                    </div>
                </div>
                
                <div class="modal-actions">
                    <button type="button" @click="$emit('close')" class="btn-cancel">
                        {{ $translate('Cancel') }}
                    </button>
                    <button type="submit" class="btn-save">
                        <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ $translate('Save') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue';
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import { translate } from '@/utils/translate';

export default {
    name: 'ShippingAddressModal',
    props: {
        address: {
            type: Object,
            required: true,
        },
    },
    emits: ['close', 'save'],
    setup(props, { emit }) {
        const localAddress = ref({ ...props.address });
        const sessionId = cookie(); // Replace with actual session ID
        const isSubmitting = ref(false);

        const saveShippingAddress = async () => {
            if (isSubmitting.value) return;
            isSubmitting.value = true;
            
            try {
                const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/attendee/shipping-address`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        name: localAddress.value.name,
                        address: localAddress.value.address,
                        city: localAddress.value.city,
                        zip_code: localAddress.value.zip_code,
                        country_id: localAddress.value.country_id,
                        brand_id: window.vueAppConfig.info.id,
                        session_id: sessionId
                    }),
                });
                const result = await response.json();
                if (result.status) {
                    emit('save', localAddress.value);
                    emit('close');
                    notify({
                        title: translate("Success"),
                        text: translate(result.message),
                        type: "info",
                        group: "info"
                    }, 15000);
                } else {
                    notify({
                        title: translate("Error"),
                        text: translate(result.message),
                        type: "error",
                        group: "error"
                    }, 10000);
                }
            } catch (error) {
                console.error('Error saving shipping address:', error);
                notify({
                    title: translate("Error"),
                    text: translate("An error occurred while saving the shipping address."),
                    type: "error",
                    group: "error"
                }, 10000);
            } finally {
                isSubmitting.value = false;
            }
        };

        watch(() => props.address, (newAddress) => {
            localAddress.value = { ...newAddress };
        });

        return {
            localAddress,
            saveShippingAddress,
            isSubmitting
        };
    },
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2147483647; /* Maximum z-index */
}

.modal-container {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    max-width: 32rem;
    width: 100%;
    margin: 1rem;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.info-alert {
    background-color: #fff8e6;
    border-left: 4px solid #f59e0b;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
    color: #92400e;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.col-span-2 {
    grid-column: span 2;
}

.form-grid label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.375rem;
}

.form-grid input, 
.form-grid select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background-color: #f9fafb;
    font-size: 0.95rem;
    color: #1f2937;
    transition: all 0.2s ease;
}

.form-grid input:focus, 
.form-grid select:focus {
    outline: none;
    ring: 2px;
    ring-color: rgba(59, 130, 246, 0.5);
    border-color: #3b82f6;
    background-color: white;
}

.select-arrow {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(12px);
    pointer-events: none;
    color: #6b7280;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

.btn-cancel {
    padding: 0.6rem 1.25rem;
    background-color: #f3f4f6;
    color: #374151;
    font-weight: 500;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
}

.btn-cancel:hover {
    background-color: #e5e7eb;
}

.btn-save {
    padding: 0.6rem 1.25rem;
    background-color: #3b82f6;
    color: white;
    font-weight: 500;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
}

.btn-save:hover {
    background-color: #2563eb;
}

.btn-save:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
}
</style>
